import React from 'react';
import {
  Bird,
  Trees,
  Coffee,
  Brain,
  Leaf,
  Sparkles,
  ArrowRight,
  Flower2,
  BookOpen,
} from 'lucide-react';
import './ForestExperience.css';

const scenes = [
  {
    icon: <Bird className="forest-scene-icon" />,
    number: '場景一',
    title: '看到一隻鳥正在吃桑椹',
    subtitle: '與生命共存',
    quote: '你不是在跟市場競爭，而是在跟「生命」共存。',
    image: '/forest_bird.jpg',
    points: ['生態自然農場', '鳥吃桑椹', '看見土地是活著的', '重新問自己：我是存在，還是活著？'],
  },
  {
    icon: <Trees className="forest-scene-icon" />,
    number: '場景二',
    title: '看一棵參天大樹',
    subtitle: '根深之後，繁茂自來',
    quote: '真正的成功，不是一夜爆紅，而是在沒有人看見時，依然默默生長。',
    image:
      'https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&q=80&w=1400',
    points: ['熱愛是種子', '環境是土壤', '失敗是肥料', '時間是陽光與四季'],
  },
  {
    icon: <Coffee className="forest-scene-icon" />,
    number: '場景三',
    title: '布農部落的午後咖啡',
    subtitle: '安靜下來，答案才會浮現',
    quote: '有些答案，不是在競爭裡找到的，而是在安靜下來後，才慢慢浮現。',
    image: '/forest_coffee.png',
    points: ['喝一杯咖啡', '聽蟲鳴鳥叫', '聊第一性原理', '聊根深曲線與人生'],
  },
  {
    icon: <Brain className="forest-scene-icon" />,
    number: '場景四',
    title: '未來學習力夏令營',
    subtitle: 'AI × 森林探索',
    quote: '讓孩子學會一生自主學習，而不是只為了考試而學習。',
    image:
      'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&q=80&w=1400',
    points: ['用 AI 練英文', '用 AI 理解數學', '用 AI 觀察植物', '用 AI 探索自然與宇宙'],
  },
  {
    icon: <Leaf className="forest-scene-icon" />,
    number: '場景五',
    title: '慢慢品嘗綠拿鐵與酵素',
    subtitle: '重新感受生命',
    quote: '很多真正好的東西，都不能太快。樹長大、土壤形成、酵素發酵、一個人成熟，都需要時間。',
    image:
      'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=1400',
    points: ['自然飲食', '綠拿鐵', '酵素發酵', '土地、陽光、時間與生命'],
  },
  {
    icon: <Trees className="forest-scene-icon" />,
    number: '場景六',
    title: '一棵樹裡，看見佛法',
    subtitle: '應無所住，而生其心',
    quote:
      '真正強大的樹，不是永遠不掉葉子，而是在風雨與四季之中，依然願意繼續生長。',
    image: '/forest_tree.jpg',
    points: [
      '六祖惠能與森林中的悟道',
      '不執著一片葉子',
      '不執著一個季節',
      '不執著一場風雨',
      '生命，本來就是流動',
      '應無所住，而生其心',
      '真正的成長，是歷經四季後仍願意扎根',
    ],
  },
  {
    icon: <Flower2 className="forest-scene-icon" />,
    number: '場景七',
    title: '禪修的目的',
    subtitle: '看清自己，拿回心的主導權',
    quote:
      '禪修不是逃避世界，而是在世界裡，保持清醒。',
    image:
      'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&q=80&w=1400',
    points: [
      '看見心是怎麼運作的',
      '在情緒與念頭之間生出覺察',
      '不被手機、股價、比較與評價綁架',
      '看見貪婪、恐懼與執著',
      '像老樹一樣向下扎根',
      '即使經歷風雨，心裡仍有一輪明月',
    ],
  
  },
 ];

const ForestExperience: React.FC = () => {
  return (
    <div className="forest-experience-page">
      <section className="forest-hero">
        <div className="forest-hero-overlay"></div>
        <div className="forest-hero-content">
          <span className="forest-hero-badge">Forest Experience｜森林體驗</span>
          <h1>
            不是來上課，
            <br />
            而是走進另一種生活方式
          </h1>
          <p>
            七大森林場景，帶你從自然、生命、學習、飲食、思考與覺察，
            重新看見自己與世界的關係。
          </p>
        </div>
      </section>

      <section className="forest-intro section-width">
        <div className="forest-section-header">
          <Sparkles className="forest-section-icon" />
          <h2>七大森林場景</h2>
        </div>
        <p>
          森林學校不是只把課程搬到自然裡，
          而是讓自然本身成為老師。
          一隻鳥、一棵樹、一杯咖啡、一杯綠拿鐵，
          都可能成為重新理解生命的入口。
        </p>
      </section>

      <section className="forest-scenes section-width">
        {scenes.map((scene, index) => (
          <article
            className={`forest-scene-card ${
              index % 2 === 1 ? 'forest-scene-reverse' : ''
            }`}
            key={scene.title}
          >
            <div className="forest-scene-image">
              <img src={scene.image} alt={scene.title} />
            </div>

            <div className="forest-scene-content">
              <div className="forest-scene-top">
                {scene.icon}
                <span>{scene.number}</span>
              </div>

              <h2>{scene.title}</h2>
              <h3>{scene.subtitle}</h3>

              <p className="forest-scene-quote">「{scene.quote}」</p>

              <ul className="forest-scene-points">
                {scene.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
             
            </div>
          </article>
        ))}
      </section>

      <section className="future-scenes section-width">
        <div className="future-scenes-card">
          <h2>未來還會增加更多森林場景</h2>
          <p>
            星空、森林裡的一場雨、沒有手機的一天、親手種下一棵樹……
            每一個場景，都是一堂生命課。
          </p>
        </div>
      </section>

      <section className="forest-cta section-width">
        <div className="forest-cta-card">
          <h2>走進森林，重新感受生命</h2>
          <p>
            這不是一次活動，而是一段慢慢回到自己的旅程。
          </p>
          <button className="forest-cta-button">
            了解課程活動 <ArrowRight className="forest-cta-icon" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default ForestExperience;